import { Component } from '@angular/core';
import { CouponFormsService } from '../../services/coupon-forms.service';
import { Router } from '@angular/router';
import { PhysicianProcedure } from '../../../../core/dtos/physician.dto';
import { CurrencyUtil } from 'app/core/utils/currency.utils';
import { CouponApiService } from '../../services/coupon-api.service';
import {
  PaymentLinkRequest,
  PaymentLinkResponse,
} from '../../dtos/payment-link.dto';
import { finalize } from 'rxjs/operators';
import { DefaultResponse } from 'app/shared/types';
import { UserRoles } from 'app/core/models/user.model';
import { CreateAppointmentRequestDTO } from '../../dtos/create-appointment-request.dto';
import { CreateAppointmentResponseDTO } from '../../dtos/create-appointment-response.dto';
import { ToastService } from 'app/core/services/toast.service';
import { COPY_PAYMENT_LINK_MESSAGES } from '../../constants/messages';

@Component({
  selector: 'app-purchase-summary',
  templateUrl: './purchase-summary.component.html',
  styleUrls: ['./purchase-summary.component.scss'],
})
export class PurchaseSummaryComponent {
  protected convertToBrazilianCurrency =
    CurrencyUtil.convertToBrazilianCurrency;

  displayModal = false;
  paymentUrl = '';
  isLoading = false;

  constructor(
    private formsService: CouponFormsService,
    private router: Router,
    private couponApiService: CouponApiService,
    private toastService: ToastService,
  ) {}

  getUserCompleteAddress(): string {
    const address = this.formsService.formAddress.value;
    return [
      address.street,
      address.number,
      address.city,
      address.state,
      address.zipCode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  getPhysicianCompleteAddress(): string {
    return [
      this.physician.addressStreet,
      this.physician.addressNumber,
      this.physician.addressNeighborhood,
      this.physician.addressCity,
      this.physician.addressState,
      this.physician.addressZipCode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  get selectedProcedures(): PhysicianProcedure[] {
    return (
      this.formsService.formProcedures.get('selectedProcedures')?.value || []
    );
  }

  get proceduresTotal(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) => acc + procedure.procedureOriginalPrice,
      0,
    );
  }

  get discountTotal(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) =>
        acc +
        (procedure.procedureOriginalPrice -
          procedure.procedureFinalPriceWithTax),
      0,
    );
  }

  get proceduresPriceWithTaxTotal(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) => acc + procedure.procedureFinalPriceWithTax,
      0,
    );
  }

  get serviceTax(): number {
    return this.selectedProcedures.reduce(
      (acc, procedure) => acc + procedure.serviceTax,
      0,
    );
  }

  get discountPercent(): number {
    if (!this.proceduresTotal) return 0;
    return (this.discountTotal / this.proceduresTotal) * 100;
  }

  get finalPrice(): number {
    return this.proceduresPriceWithTaxTotal;
  }

  get user() {
    return this.formsService.formUser.value;
  }

  get physician() {
    return this.formsService.formPhysician.value;
  }

  get appointmentDateTime() {
    return this.formsService.formAppointment.get('appointmentDateTime')?.value;
  }

  get info() {
    return this.formsService.formInfo.value;
  }

  goBack() {
    this.router.navigate(['/coupon/procedures']);
  }

  getAppointmentDatetime() {
    const appointmentValue = this.appointmentDateTime;

    if (!appointmentValue || !appointmentValue.date || !appointmentValue.time) {
      return new Date();
    }

    const year = appointmentValue.date.getFullYear();
    const month = appointmentValue.date.getMonth();
    const day = appointmentValue.date.getDate();
    const hours = appointmentValue.time.getHours();
    const minutes = appointmentValue.time.getMinutes();

    return new Date(year, month, day, hours, minutes, 0, 0);
  }

  createAppointment() {
    this.isLoading = true;

    const userAddress = this.formsService.formAddress.value;

    const payload: CreateAppointmentRequestDTO = {
      customer: {
        id: this.user.id!,
        name: this.user.name!,
        cpf: this.user.cpf!,
        birthDate: this.user.birthDate!,
        email: this.user.email!,
        phone: this.user.phone!,
        pushToken: this.user.pushToken!,
        roles: this.user.roles?.length
          ? this.user.roles
          : [UserRoles.Beneficiary],
        s4eUserId: this.user.s4eUserId!,
        address: [
          {
            id: userAddress.id!,
            title: userAddress.title || 'Principal',
            zipCode: userAddress.zipCode!,
            state: userAddress.state!,
            city: userAddress.city!,
            neighborhood: userAddress.neighborhood!,
            street: userAddress.street!,
            number: userAddress.number!,
            complement: userAddress.complement || '',
            reference: userAddress.reference || '',
            latitude:
              (userAddress.latitude && Number(userAddress.latitude)) || 0,
            longitude:
              (userAddress.longitude && Number(userAddress.longitude)) || 0,
          },
        ],
      },
      physician: {
        physicianId: this.physician.physicianId!,
        physicianName: this.physician.physicianName!,
        physicianCpf: this.physician.physicianCpf!,
        physicianBirthDate: this.physician.physicianBirthDate!,
        physicianCro: this.physician.physicianCro!,
        physicianPhone: this.physician.physicianPhone!,
        physicianEmail: this.physician.physicianEmail!,
        physicianBranchCode: this.physician.physicianBranchCode!,
        physicianBranchName: this.physician.physicianBranchName!,
        addressStreet: this.physician.addressStreet!,
        addressNumber: this.physician.addressNumber!,
        addressNeighborhood: this.physician.addressNeighborhood!,
        addressCity: this.physician.addressCity!,
        addressState: this.physician.addressState!,
        addressZipCode: this.physician.addressZipCode!,
        longitude: this.physician.longitude!,
        latitude: this.physician.latitude!,
        distanceInKilometers: this.physician.distanceInKilometers!,
        score: this.physician.score!,
        accredited: this.physician.accredited!,
      },
      datetimeToAppointment: this.getAppointmentDatetime(),
    };

    this.couponApiService
      .createAppointment(payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: DefaultResponse<CreateAppointmentResponseDTO>) => {
          if (response && !response.error && response.response) {
            this.displayModal = true;
          }
        },
        error: error => {
          console.error(
            'Erro ao agendar consulta de usuário com cupom já comprado',
            error,
          );
        },
      });
  }

  generateLink() {
    this.isLoading = true;

    const userAddress = this.formsService.formAddress.value;

    const payload: PaymentLinkRequest = {
      customer: {
        id: this.user.id!,
        name: this.user.name!,
        cpf: this.user.cpf!,
        birthDate: this.user.birthDate!,
        email: this.user.email!,
        phone: this.user.phone!,
        pushToken: this.user.pushToken!,
        roles: this.user.roles?.length
          ? this.user.roles
          : [UserRoles.Beneficiary],
        s4eUserId: this.user.s4eUserId!,
        address: [
          {
            id: userAddress.id!,
            title: userAddress.title || 'Principal',
            zipCode: userAddress.zipCode!,
            state: userAddress.state!,
            city: userAddress.city!,
            neighborhood: userAddress.neighborhood!,
            street: userAddress.street!,
            number: userAddress.number!,
            complement: userAddress.complement || '',
            reference: userAddress.reference || '',
            latitude:
              (userAddress.latitude && Number(userAddress.latitude)) || 0,
            longitude:
              (userAddress.longitude && Number(userAddress.longitude)) || 0,
          },
        ],
      },
      physician: {
        physicianId: this.physician.physicianId!,
        physicianName: this.physician.physicianName!,
        physicianCpf: this.physician.physicianCpf!,
        physicianBirthDate: this.physician.physicianBirthDate!,
        physicianCro: this.physician.physicianCro!,
        physicianPhone: this.physician.physicianPhone!,
        physicianEmail: this.physician.physicianEmail!,
        physicianBranchCode: this.physician.physicianBranchCode!,
        physicianBranchName: this.physician.physicianBranchName!,
        addressStreet: this.physician.addressStreet!,
        addressNumber: this.physician.addressNumber!,
        addressNeighborhood: this.physician.addressNeighborhood!,
        addressCity: this.physician.addressCity!,
        addressState: this.physician.addressState!,
        addressZipCode: this.physician.addressZipCode!,
        longitude: this.physician.longitude!,
        latitude: this.physician.latitude!,
        distanceInKilometers: this.physician.distanceInKilometers!,
        score: this.physician.score!,
        accredited: this.physician.accredited!,
      },
      procedures: this.selectedProcedures,
      datetimeToAppointment: this.getAppointmentDatetime(),
    };

    this.couponApiService
      .generatePaymentLink(payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: DefaultResponse<PaymentLinkResponse>) => {
          if (response && !response.error && response.response) {
            this.paymentUrl = response.response.paymentLink;
            this.displayModal = true;
          }
        },
        error: error => {
          console.error('Erro ao gerar link de pagamento', error);
        },
      });
  }

  closeModal() {
    this.displayModal = false;
    this.formsService.resetForms();
    this.router.navigate(['/coupon']);
  }

  async copyLink() {
    if (!this.paymentUrl) return;

    try {
      const canUseClipboardApi =
        typeof navigator !== 'undefined' &&
        !!navigator.clipboard &&
        typeof window !== 'undefined' &&
        window.isSecureContext;

      if (canUseClipboardApi) {
        await navigator.clipboard.writeText(this.paymentUrl);
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = this.paymentUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!successful) {
          throw new Error(COPY_PAYMENT_LINK_MESSAGES.fallbackFailed);
        }
      } else {
        throw new Error(COPY_PAYMENT_LINK_MESSAGES.clipboardUnavailable);
      }

      this.toastService.showSuccessToast(
        '',
        COPY_PAYMENT_LINK_MESSAGES.success,
      );
    } catch (error) {
      console.error('Erro ao copiar link de pagamento', error);
      this.toastService.showErrorToast('', COPY_PAYMENT_LINK_MESSAGES.error);
    }
  }
}
